// The one glanceable balance: "1,250 credits" as a quiet pill. Tapping it
// opens a small popover — the balance, the plan, when the next credits
// arrive, and two plain actions: Buy credits (the TopUpSheet, from anywhere)
// and Account. Renders nothing when signed out or when the API returned no
// credits block. A FAILED fetch shows "—" rather than a number — unknown is
// never zero.

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { formatCredits } from "../../constants/credits";
import { creditsDateLine, planLabelFor } from "../../containers/AccountPage/creditsCopy";
import { useAccount } from "../../contexts/AccountContext";
import { getToken } from "../../utils/auth";
import TopUpSheet from "./TopUpSheet";

const Wrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--brand-primary-50, #e6f3f2);
  color: var(--brand-primary-700, #085552);
  border: none;
  font: inherit;
  font-size: ${(p) => (p.$variant === "compact" ? "12.5px" : "13.5px")};
  font-weight: 600;
  line-height: 1;
  border-radius: 999px;
  padding: ${(p) => (p.$variant === "compact" ? "6px 10px" : "8px 13px")};
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: var(--brand-primary-100, #cfe8e6);
  }
`;

const Popover = styled.section`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 1050;
  width: 280px;
  background: #fff;
  border: 1px solid var(--brand-border, #e3ebec);
  border-radius: 14px;
  box-shadow: var(--brand-shadow-lg, 0 12px 32px rgba(13, 59, 57, 0.1));
  padding: 14px 16px 12px;
  text-align: left;
  white-space: normal;
`;

const Balance = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: var(--brand-ink, #0b1c1e);
  line-height: 1.2;
`;

const Meta = styled.p`
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--brand-ink-3, #5c7074);
  margin: 4px 0 0;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
`;

const BuyButton = styled.button`
  background: var(--brand-primary, #0e7e7a);
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 8px 16px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: var(--brand-primary-600, #0b6360);
  }
`;

const AccountLink = styled(Link)`
  font-size: 13.5px;
  font-weight: 600;
  color: var(--brand-primary-700, #085552) !important;
  text-decoration: none !important;

  &:hover {
    text-decoration: underline !important;
  }
`;

/**
 * @param {{ compact?: boolean, className?: string }} props
 *   compact: the editor save-bar variant (smaller type and padding).
 */
const BalanceChip = ({ compact = false, className }) => {
  const { credits, error, loading } = useAccount();
  const [open, setOpen] = useState(false);
  const [buying, setBuying] = useState(false);
  const wrapRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const chipRef = useRef(/** @type {HTMLButtonElement | null} */ (null));

  const close = useCallback(() => setOpen(false), []);

  // Click-away and Escape close the popover.
  useEffect(() => {
    if (!open) return undefined;
    /** @param {MouseEvent} e */
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(/** @type {Node} */ (e.target))) close();
    };
    /** @param {KeyboardEvent} e */
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  if (!getToken()) return null;
  if (!credits && !error) return null; // loading, or the API returned no credits block
  const label = credits ? formatCredits(credits.balance) : "— credits";

  return (
    <Wrap ref={wrapRef} className={className}>
      <Chip
        ref={chipRef}
        type="button"
        $variant={compact ? "compact" : "default"}
        aria-label={credits ? `Balance: ${label}` : "Balance unavailable"}
        aria-busy={loading || undefined}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid="balance-chip"
      >
        {label}
      </Chip>
      {open && (
        <Popover aria-label="Your credits" data-testid="balance-popover">
          <Balance>{label}</Balance>
          {credits ? (
            <>
              <Meta>{planLabelFor(credits.plan_key)}</Meta>
              <Meta>{creditsDateLine(credits)}</Meta>
            </>
          ) : (
            <Meta>Your balance isn't available right now.</Meta>
          )}
          <Actions>
            <BuyButton
              type="button"
              onClick={() => {
                close();
                setBuying(true);
              }}
            >
              Buy credits
            </BuyButton>
            <AccountLink to="/account#credits" onClick={close}>
              Account
            </AccountLink>
          </Actions>
        </Popover>
      )}
      {buying && (
        <TopUpSheet
          balance={credits ? credits.balance : null}
          onClose={() => setBuying(false)}
          returnFocusRef={chipRef}
        />
      )}
    </Wrap>
  );
};

export default BalanceChip;
